import { useTranslation } from "react-i18next";

import { MediaTypeImage } from "../../../components/mediaTypeImage";
import {
    ASSET_PREVIEW_STATUS,
    ASSET_THUMBNAIL_CARD_CONTAINER_SIZE,
    ASSET_THUMBNAIL_TABLE_CONTAINER_SIZE,
    MEDIA_TYPES,
} from "@constants";
import { Box, Typography } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { alpha } from "@mui/material";
import { IconLock, IconViewDetails } from "@mzc-cloudplex/icons";
import { useMemo } from "react";
import useThumbnailCrop from "@hooks/useThumbnailCrop";
import ImagePreviewV2 from "@components/modals/image-preview-v2";

const MediaImage = ({
    variant,
    size,
    errorIconProps,
    asset,
    draggable,
    thumbnailCrop,
    isShowImagePreviewModal,
    handleShowImagePreviewModal,
    handleCloseImagePreviewModal,
}) => {
    const { t } = useTranslation();

    const commonStatusPreviews = asset.previews?.filter((p) => p.status !== ASSET_PREVIEW_STATUS.ERROR);
    let src = commonStatusPreviews?.[0]?.accessUrl;
    if (!src) {
        if (asset.mediaType === MEDIA_TYPES.IMAGE) src = asset.accessUrls && asset.accessUrls[0];
        else if (asset.mediaType === MEDIA_TYPES.VIDEO)
            src = asset.thumbnails && asset.thumbnails[0] && asset.thumbnails[0].accessUrl;
    }

    const previewStatus = useMemo(() => {
        // NOTE : 모든 프리뷰가 Error일 경우에만 Error로 표시
        if (commonStatusPreviews?.length === 0 && asset.previews?.length > 0) return ASSET_PREVIEW_STATUS.ERROR;
        else return commonStatusPreviews?.[0]?.status ?? ASSET_PREVIEW_STATUS.COMPLETE;
    }, [asset]);

    const { previewContainerStyle, previewImageStyle } = useThumbnailCrop({
        thumbnailCrop,
        variant,
        ignorePadding: asset.state === "DRAFT" || previewStatus === ASSET_PREVIEW_STATUS.IN_PROGRESS,
        cardContainerSize: ASSET_THUMBNAIL_CARD_CONTAINER_SIZE,
        tableContainerSize: ASSET_THUMBNAIL_TABLE_CONTAINER_SIZE,
    });

    const previewModalSrc = useMemo(() => {
        return commonStatusPreviews?.[commonStatusPreviews?.length - 1]?.accessUrl;
    }, [commonStatusPreviews]);

    return (
        <Box
            draggable={draggable}
            sx={{
                position: `relative`,
                ".btn-preview": {
                    opacity: 0,
                },
                "&:hover .btn-preview": {
                    opacity: 1,
                },
                ...previewContainerStyle,
            }}
        >
            <MediaTypeImage
                mediaType={asset.mediaType}
                state={asset.state}
                src={src}
                size={size}
                errorIconProps={errorIconProps}
                previewStatus={previewStatus}
                draggable={draggable}
                variant={variant}
                sx={previewImageStyle}
                width={variant === "table" ? 28 : 46}
                height={variant === "table" ? 28 : 56}
            />
            {src &&
                previewStatus === ASSET_PREVIEW_STATUS.COMPLETE &&
                isShowImagePreviewModal !== undefined &&
                handleShowImagePreviewModal && (
                    <>
                        <Box
                            className={"btn-preview"}
                            role="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleShowImagePreviewModal();
                            }}
                            gap={1}
                            sx={{
                                opacity: 1,
                                position: `absolute`,
                                left: `50%`,
                                top: `50%`,
                                display: `flex`,
                                flexDirection: `column`,
                                alignItems: `center`,
                                justifyContent: `center`,
                                width: variant === "table" ? 32 : 100,
                                height: variant === "table" ? 32 : 100,
                                margin: variant === "table" ? `-16px 0 0 -16px` : `-50px 0 0 -50px`,
                                color: theme.palette.common.white,
                                backgroundColor: alpha(theme.palette.constant.black, 0.6),
                                borderRadius: `100%`,
                                transition: `opacity 0.2s linear`,
                                cursor: `pointer`,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 16,
                                    height: 16,
                                    display: `flex`,
                                    alignItems: `center`,
                                    justifyContent: `center`,
                                    borderRadius: `50%`,
                                    color: theme.palette.constant.white,
                                    backgroundColor: theme.palette.etc.gray500,
                                }}
                            >
                                <IconViewDetails size={20} />
                            </Box>
                            {variant !== "table" && (
                                <Typography variant={"body4_500"} color={theme.palette.constant.white}>
                                    {t(`common::label::Image`)} {t(`common::label::Zoom in`)}
                                </Typography>
                            )}
                        </Box>
                    </>
                )}

            {isShowImagePreviewModal && (
                <ImagePreviewV2 asset={asset} onClickClose={handleCloseImagePreviewModal} src={previewModalSrc} />
            )}
        </Box>
    );
};

export default MediaImage;
